import React, { useState, useRef, useEffect } from 'react'
import { X, Camera, Clock, LogOut, Save, Mail, Shield, Trash2, Upload } from 'react-feather'
import { useAuth } from '../../Context/AuthContext'
import { useData } from '../../Context/DataContext'
import { TimeZoneOptions } from '../../Options/TimeZoneOptions'
import axios from 'axios'
import baseUrl from '../../Utils/baseUrl'
import { useNavigate } from 'react-router-dom'

interface ProfileModalProps {
  isOpen: boolean
  onClose: () => void
}

const ProfileModal: React.FC<ProfileModalProps> = ({ isOpen, onClose }) => {
  const { currentProfile, currentUser, grabCurrentUserProfile } = useAuth()
  const { currentTimeZone, handleTimeZoneChange } = useData()
  const navigate = useNavigate()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [name, setName] = useState(currentProfile.name || '')
  const [selectedTimezone, setSelectedTimezone] = useState(currentTimeZone)
  const [isUploading, setIsUploading] = useState(false)
  const [isRemoving, setIsRemoving] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [profilePhoto, setProfilePhoto] = useState(currentProfile.profile_photo || '')
  const [hasChanges, setHasChanges] = useState(false)
  const [showPhotoMenu, setShowPhotoMenu] = useState(false)

  // Update state when profile changes
  useEffect(() => {
    setName(currentProfile.name || '')
    setProfilePhoto(currentProfile.profile_photo || '')
  }, [currentProfile])

  useEffect(() => {
    setSelectedTimezone(currentTimeZone)
  }, [currentTimeZone])

  // Track changes
  useEffect(() => {
    const nameChanged = name !== (currentProfile.name || '')
    const timezoneChanged = selectedTimezone !== currentTimeZone
    setHasChanges(nameChanged || timezoneChanged)
  }, [name, selectedTimezone, currentProfile.name, currentTimeZone])

  if (!isOpen) return null

  const handlePhotoClick = () => {
    setShowPhotoMenu(!showPhotoMenu)
  }

  const handleUploadClick = () => {
    setShowPhotoMenu(false)
    fileInputRef.current?.click()
  }

  const handleRemovePhoto = async () => {
    if (!profilePhoto) return

    setShowPhotoMenu(false)
    setIsRemoving(true)

    try {
      const response = await axios.put(
        `${baseUrl}/users/${currentUser.userId}/profile`,
        { profile_photo: null }
      )

      if (response.data.status === 'Success') {
        setProfilePhoto('')
        if (currentUser.userId) {
          grabCurrentUserProfile(currentUser.userId)
        }
      }
    } catch (error) {
      console.error('Failed to remove photo:', error)
      alert('Failed to remove photo. Please try again.')
    } finally {
      setIsRemoving(false)
    }
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB')
      return
    }

    setIsUploading(true)

    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await axios.post(
        `${baseUrl}/users/${currentUser.userId}/profile-photo`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      )

      if (response.data.status === 'Success') {
        setProfilePhoto(response.data.profile_photo_url)
        if (currentUser.userId) {
          grabCurrentUserProfile(currentUser.userId)
        }
      }
    } catch (error) {
      console.error('Failed to upload photo:', error)
      alert('Failed to upload photo. Please try again.')
    } finally {
      setIsUploading(false)
    }
  }

  const handleSave = async () => {
    if (!hasChanges) return

    setIsSaving(true)

    try {
      const response = await axios.put(
        `${baseUrl}/users/${currentUser.userId}/profile`,
        { name, timezone: selectedTimezone }
      )

      if (response.data.status === 'Success') {
        handleTimeZoneChange(selectedTimezone)
        if (currentUser.userId) {
          grabCurrentUserProfile(currentUser.userId)
        }
        setHasChanges(false)
      }
    } catch (error) {
      console.error('Failed to update profile:', error)
      alert('Failed to update profile. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }

  const handleLogout = () => {
    onClose()
    navigate('/auth/logout')
  }

  const getInitials = (name: string | null) => {
    if (!name) return 'U'
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-md bg-dark-card border border-dark-border rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
        {/* Header */}
        <div className="relative h-24 bg-gradient-to-r from-accent-teal via-accent-purple to-accent-deep-purple">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 p-1.5 rounded-lg bg-black/20 hover:bg-black/40 transition-colors"
          >
            <X size={18} className="text-white" />
          </button>
        </div>

        {/* Profile Photo */}
        <div className="relative -mt-12 flex justify-center">
          <div className="relative">
            <div
              onClick={handlePhotoClick}
              className="relative group cursor-pointer"
            >
              {profilePhoto ? (
                <img
                  src={profilePhoto}
                  alt="Profile"
                  className="w-24 h-24 rounded-full border-4 border-dark-card object-cover"
                />
              ) : (
                <div className="w-24 h-24 rounded-full border-4 border-dark-card bg-gradient-to-br from-accent-teal to-accent-purple flex items-center justify-center">
                  <span className="text-2xl font-bold text-white">
                    {getInitials(currentProfile.name)}
                  </span>
                </div>
              )}
              <div className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                {(isUploading || isRemoving) ? (
                  <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Camera size={24} className="text-white" />
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
            </div>

            {/* Photo Options Menu */}
            {showPhotoMenu && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowPhotoMenu(false)}
                />
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-dark-card border border-dark-border rounded-xl shadow-xl overflow-hidden z-20 w-44">
                  <button
                    onClick={handleUploadClick}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-white hover:bg-dark-card-hover transition-colors"
                  >
                    <Upload size={16} className="text-accent-teal" />
                    Upload Photo
                  </button>
                  {profilePhoto && (
                    <button
                      onClick={handleRemovePhoto}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-400 hover:bg-red-500/10 transition-colors border-t border-dark-border"
                    >
                      <Trash2 size={16} />
                      Remove Photo
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 pt-4">
          {/* Name - Always Editable */}
          <div className="text-center mb-6">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="text-xl font-bold text-white bg-transparent border-b-2 border-transparent hover:border-dark-border focus:border-accent-teal px-2 py-1 text-center w-full focus:outline-none transition-colors"
              placeholder="Your Name"
            />
            <p className="text-sm text-gray-400 mt-1 capitalize">{currentProfile.department} Department</p>
          </div>

          {/* Info Cards */}
          <div className="space-y-3">
            {/* Email */}
            <div className="flex items-center gap-3 p-3 bg-dark-input rounded-xl">
              <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-accent-teal/20">
                <Mail size={18} className="text-accent-teal" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500">Email</p>
                <p className="text-sm text-white truncate">{currentProfile.email}</p>
              </div>
            </div>

            {/* Role */}
            <div className="flex items-center gap-3 p-3 bg-dark-input rounded-xl">
              <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-accent-purple/20">
                <Shield size={18} className="text-accent-purple" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-gray-500">Role</p>
                <p className="text-sm text-white capitalize">{currentProfile.privileges}</p>
              </div>
            </div>

            {/* Timezone - Always Editable */}
            <div className="flex items-center gap-3 p-3 bg-dark-input rounded-xl">
              <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-accent-orange/20">
                <Clock size={18} className="text-accent-orange" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-gray-500">Timezone</p>
                <select
                  value={selectedTimezone}
                  onChange={(e) => setSelectedTimezone(e.target.value)}
                  className="w-full text-sm text-white bg-transparent border-none focus:outline-none cursor-pointer"
                >
                  {TimeZoneOptions.map((tz) => (
                    <option key={tz} value={tz} className="bg-dark-card">{tz}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-6 space-y-2">
            <button
              onClick={handleSave}
              disabled={isSaving || !hasChanges}
              className={`w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl font-medium transition-all ${
                hasChanges
                  ? 'bg-gradient-to-r from-accent-teal to-accent-purple text-white hover:opacity-90'
                  : 'bg-dark-input text-gray-500 cursor-not-allowed'
              }`}
            >
              {isSaving ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Save size={16} />
                  Save Profile
                </>
              )}
            </button>

            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors"
            >
              <LogOut size={16} />
              Sign Out
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ProfileModal
